import './BulletinsTable.scss';
import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { LoadingCircle } from '@gplibraries/react/components';
import { useConfig } from '@gplibraries/react/hooks';
import { useSearchParams } from 'react-router';
import BulletinsTableFilters from './BulletinsTableFilters.jsx';

export default function BulletinsTable(props) {

  const {
    bulletins,
    loading,
    error,
    pageLimit = 25,
    hideFilters = false,
    hideFields = [],
    minWidth = '690px',
    setPageTitle
  } = props

  const columnsConfig = useConfig('bulletins.columns');
  const [params, setParams] = useSearchParams();
  const [sortDir, setSortDir] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const [filteredBulletins, setFilteredBulletins] = useState(bulletins?.data);

  // helper for updating query params when filters/sort are updated.
  const updateParams = (newParams) => {
    const nextParams = new URLSearchParams(params);
    for (const param in newParams) {
      // If the param value changed, update it.
      if (newParams[param] && nextParams.get(param) !== newParams[param]) {
        nextParams.set(param, newParams[param]);
      }
      // If the new param value is null, remove it.
      else if (nextParams.get(param) && newParams[param] === null) {
        nextParams.delete(param);
      }
    }
    setParams(nextParams);
  }

  // populate filtered bulletins when the bulletins data is loaded.
  useEffect(() => {
    if (bulletins?.data && !loading) {
      setFilteredBulletins(bulletins?.data);
    }
  }, [bulletins]);

  // Set filter/sort from query parameters, if present.
  useEffect(() => {
    if(params.size === 0) return;
    if (params.has('sort')) setSortField(params.get('sort'));
    if (params.has('sortDir')) setSortDir(params.get('sortDir'));
    setFilters({
      category: params.has('type') ? params.get('type').replace('+', ' ') : null,
      status: params.has('status') ? params.get('status') : null
    });
  }, []);

  // any time the filter/sort state is updated:
  //   - re-sort/filter the bulletins
  //   - reset the number of pages loaded
  //   - update query parameters
  //   - update page title
  useEffect(() => {
    if (!bulletins) return;
    setFilteredBulletins(bulletins.data.filter(filterFunction).sort(sortFunction));
    setPagesLoaded(1);
    updateParams({
      type: filters?.category,
      status: filters?.status,
      sortDir: sortDir,
      sort: sortField
    })

    if (setPageTitle) {
      setPageTitle(filters?.category ? `Bulletins: ${filters.category}` : 'Bulletins');
    }
  }, [bulletins, filters, sortField, sortDir]);

  // sorting logic
  const sortFunction = (a, b) => {
    const fieldToSortBy = sortField || 'field_publication_date'; // use this by default if no sort is provided
    if ((sortDir || 'DESC').toUpperCase() === 'ASC') [a,b] = [b,a];
    if (fieldToSortBy === 'field_bulletin_category') return a[fieldToSortBy].name.localeCompare(b[fieldToSortBy]?.name);
    if (fieldToSortBy.includes('date')) return (b[fieldToSortBy] ?? '0').localeCompare((a[fieldToSortBy]) ?? '0');
    return a?.[fieldToSortBy]?.trim()?.localeCompare(b?.[fieldToSortBy]?.trim()) ?? 1;
  }

  // filtering logic, add more conditionals as more filtering criteria are added.
  const filterFunction = (bulletin) => {
    const archiveDate = bulletin.field_archive_date ? new Date(bulletin.field_archive_date) : null;
    const now = new Date();
    if (
      filters?.category &&
      filters.category !== bulletin.field_bulletin_category.name
    ) return false;
    if (
      filters?.status === 'active' &&
      archiveDate &&
      archiveDate <= now
    ) return false;
    if (
      filters?.status === 'archived' &&
      (!archiveDate || archiveDate > now)
    ) return false;
    // return true if the item passes all the filters
    return true;
  }

  // errors caught by error boundary
  if (error) throw error;
  if (loading) return <LoadingCircle className={'large color'} />;

  return (<>
    <div className={'flex-col bulletins-table-container'}>

      {(bulletins?.data && !hideFilters) && <BulletinsTableFilters
        categories={[...new Set(bulletins.data.map(b => b.field_bulletin_category.name))]}
        sortFields={columnsConfig}
        sortField={sortField}
        setSortField={setSortField}
        sortDir={sortDir}
        setSortDir={setSortDir}
        filters={filters}
        setFilters={setFilters}
      />}

      <table className={'full bulletins-table'} style={{minWidth: minWidth}}>

        <thead>
        <tr>
          {Object.keys(columnsConfig).map(column => {
            return !hideFields.includes(column)
              ? <th className={`${column}-col`} key={column}>{columnsConfig[column].label}</th>
              : null
          })}
        </tr>
        </thead>

        <tbody>

        {filteredBulletins &&
          filteredBulletins.map((bulletin, index) => {
            if (index < (pagesLoaded * pageLimit)) return (
              <tr key={bulletin.id}>
                {!hideFields.includes('title') && <td><Link to={`${bulletin.field_bulletin_file?.uri ?? ''}`} target={'_blank'}>{bulletin.title.trim()}</Link></td>}
                {!hideFields.includes('bulletin_id') && <td>{bulletin.field_bulletin_id}</td>}
                {!hideFields.includes('category') && <td>{bulletin.field_bulletin_category.name}</td>}
                {!hideFields.includes('posted') && <td>{new Date(bulletin.field_publication_date + "T00:00:00").toLocaleDateString('en-US')}</td>}
              </tr>
            )
          })
        }

        </tbody>

      </table>

      {filteredBulletins?.length > (pagesLoaded * pageLimit) &&
        <div className={'flex-row full center'}>
          <button onClick={() => setPagesLoaded(pagesLoaded + 1)}>Load More</button>
        </div>
      }

    </div>

  </>);
}